import type { CandidacyOrigin, CandidacyReview, ClaimStatus } from "@shared/types";
import { useT } from "../i18n";
import type { MessageKey } from "../i18n/en";

interface BadgeSpec {
  text: MessageKey;
  cls: string;
  tip: MessageKey;
}

/** Provenance — how the campaign content was produced. */
const ORIGIN: Record<CandidacyOrigin, BadgeSpec> = {
  imported: { text: "badge.imported", cls: "bg-purple-500/15 text-purple-300", tip: "badge.imported.tip" },
  declared: { text: "badge.declared", cls: "bg-muted/15 text-muted", tip: "badge.declared.tip" },
};

/** Trust — whether the WarEra player behind this campaign proved they own the
 * account. Only the positive (`claimed`) state renders a badge. */
const CLAIM: Partial<Record<ClaimStatus, BadgeSpec>> = {
  claimed: { text: "badge.verified", cls: "bg-accent/15 text-blue-300", tip: "badge.verified.tip" },
};

/** Moderation / visibility — admin-only; `published` shows nothing. */
const REVIEW: Partial<Record<CandidacyReview, BadgeSpec>> = {
  pending: { text: "badge.pending", cls: "bg-amber-500/15 text-amber-300", tip: "badge.pending.tip" },
  hidden: { text: "badge.hidden", cls: "bg-warn/15 text-warn", tip: "badge.hidden.tip" },
};

function Badge({ spec }: { spec: BadgeSpec }) {
  const t = useT();
  return (
    <span className="relative inline-block group align-middle">
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full cursor-help ${spec.cls}`}>{t(spec.text)}</span>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-30 w-56
                   rounded-lg border border-border bg-panel2 p-2 text-[11px] font-normal leading-snug text-muted
                   text-left shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {t(spec.tip)}
      </span>
    </span>
  );
}

/** Public provenance badge (origin axis). */
export function OriginBadge({ origin }: { origin: CandidacyOrigin }) {
  return <Badge spec={ORIGIN[origin]} />;
}

/** Public trust badge (claim axis); renders nothing for an unverified user. */
export function ClaimBadge({ claimStatus }: { claimStatus: ClaimStatus }) {
  const spec = CLAIM[claimStatus];
  return spec ? <Badge spec={spec} /> : null;
}

/** Admin moderation badge (review axis); renders nothing once published. */
export function ReviewBadge({ review }: { review: CandidacyReview }) {
  const spec = REVIEW[review];
  return spec ? <Badge spec={spec} /> : null;
}
